import React, { useState } from "react"
import { useMutation, useQuery } from "@apollo/client"
import { useNavigate, useParams } from "react-router-dom"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { useDispatch } from "react-redux"

import GET_ORDER from "graphql/queries/order/GetOrder"
import RESEND_ORDER_EVENT from "graphql/mutations/order/ResendOrderEvent"

import ErrorMessage from "components/Ui/Messages/ErrorMessage"
import Box from "components/Ui/Box"
import OrderPayment from "./OrderInfo/Payment/OrderPayment"
import OrderDetails from "./OrderInfo/OrderDetails"
import OrderDiscount from "./OrderInfo/Discount/OrderDiscount"
import Shipping from "./OrderInfo/Shipping/OrderShipping"
import CopyButton from "components/Ui/CopyButton"
import Giftcard from "./OrderInfo/Giftcard/Default"
import Popup from "components/Ui/Popup"
import Promotion from "./OrderInfo/Promotion/Default"
import { ActionDropdownButton } from "components/Ui/Buttons/ActionDropdownButton"

import { ReactComponent as ArrowIcon } from "images/icons/arrows-repeat.svg"
import { ReactComponent as CopyIcon } from "images/icons/copy.svg"
import { ReactComponent as SettingsIcon } from "images/icons/gear.svg"
import Bonus from "./OrderInfo/Bonus/Default"
import { CustomReasonCode } from "lib/types/generated/graphql-types"
import Updates from "./OrderInfo/Updates/Updates"
import { getJson } from "helpers/getJson"
import { ReactComponent as CodeIcon } from "images/icons/brackets-curly.svg"
import Vouchers from "./OrderInfo/Vouchers/Vouchers"
import { Deliveries } from "../../../classes/order/Deliveries"
import { Cancellations } from "../../../classes/order/Cancellations"
import { ModificationAddresses } from "../../../classes/order/OrderModificationAddresses"
import { ReactComponent as AcademyIcon } from "images/icons/graduation-cap.svg"
import {
  AcademyButton,
  ActionButtonWrapper,
  ButtonWrapper,
  Columns,
  Container,
  EditWrapper,
  Header,
  Id,
  MerchantRef,
  TabAlert,
  TabListWrapper,
  TabsContainer,
  TopContainer
} from "./ViewOrder.styled"
import { AddressType } from "lib/types/order"
import ALL_REASON_CODES from "../../../graphql/queries/settings/reasonCodes/AllReasonCodes"
import { Maybe } from "@graphql-tools/utils"
import OrderHeader from "../../../components/Order/OrderHeader/OrderHeader"
import SecondaryButton from "../../../components/Ui/Buttons/SecondaryButton"
import ALL_STORE_GROUPS from "../../../graphql/queries/store/AllStoreGroups"
import { useOrderHistoryState } from "../../../components/Order/hooks/useOrderHistoryState"
import { WithAuth } from "../../../components/Auth"
import { PermissionGroups } from "../../../routes/constants"
import DeleteOrder from "../../../components/Order/DeleteOrder"
import { useUserGroups } from "../../../hooks/useUserGroups"
import { GoBackLink } from "../../../components/Ui/Page/PageHeader.styled"
import { ReactComponent as ArrowLeft } from "images/icons/arrow-left.svg"
import { OrderTabProvider } from "./OrderTabContext"
import OrderHistoryLog from "../../../components/Order/OrderHistoryLog/OrderHistoryLog"
import ViewOrderLoading from "./ViewOrderLoading"
import ViewOrderError from "./ViewOrderError"
import { isValidUUID } from "../../../helpers/validation"

export const ViewOrder = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)

  const isValidOrderId = isValidUUID(id)

  const { data, loading, error, refetch } = useQuery(GET_ORDER, {
    variables: { id },
    fetchPolicy: "network-only",
    skip: !isValidOrderId
  })
  const { data: reasonCodesData } = useQuery(ALL_REASON_CODES, { skip: !isValidOrderId })
  const [showPopup, setShowPopup] = useState(false)
  const [resendOrderEventError, setResendOrderEventError] = useState("")

  const { data: storeGroupData } = useQuery(ALL_STORE_GROUPS, { skip: !isValidOrderId })

  const dispatch = useDispatch()

  const [resendOrderEvent, { loading: resendLoading }] = useMutation(RESEND_ORDER_EVENT, {
    onCompleted: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Order event successfully sent",
          type: "success"
        })
      )
      setShowPopup(false)
    },
    onError: () => {
      setResendOrderEventError(
        "An error occured when trying to resend the order event for the provided order ID."
      )
    }
  })

  const {
    orderHistoryList,
    setOrderHistoryList,
    customStateList,
    setCustomStateList,
    customStates,
    getCustomState,
    addCustomStateToHistory,
    error: stateError
  } = useOrderHistoryState(data?.Order?.details, data?.Order?.history)

  const { hasAnyGroup } = useUserGroups()

  if (loading) return <ViewOrderLoading />

  if (error || !isValidOrderId) {
    const message = error ? (
      <>
        Error loading Order. Try refreshing the page, or contact{" "}
        <a href="mailto:support@brinkcommerce.com">support</a>.
      </>
    ) : (
      <>
        Invalid order ID format. Please check the URL and try again, or{" "}
        <a href="/orders">return to orders list</a>.
      </>
    )
    return (
      <>
        <ViewOrderError />
        <ErrorMessage showRefreshButton message={message} />
      </>
    )
  }

  const order = data?.Order

  const modificationsAddresses = new ModificationAddresses(order?.details?.Modifications)
  const deliveries = new Deliveries(order?.details?.Deliveries)
  const cancellations = new Cancellations(order?.details?.Cancellations)

  const customReasons = reasonCodesData?.Order?.customReasonCodes?.codes

  const getCustomCode = (code: Maybe<string> | undefined) =>
    customReasons?.find((customCode: CustomReasonCode) => customCode.code === code)

  const showPromotions = () => {
    return (
      order?.details?.Promotion?.providerId !== "" ||
      order?.details?.Promotion?.providerName !== "" ||
      order?.details?.Promotion?.status !== ""
    )
  }

  const showVouchers = () => {
    return (
      order?.details?.Voucher?.providerId !== "" ||
      order?.details?.Voucher?.providerName !== "" ||
      order?.details?.Voucher?.status !== ""
    )
  }
  const hasDiscountAccess = hasAnyGroup(PermissionGroups.discounts.read)

  const showDiscounts = order?.details?.totals?.discountTotal > 0 && hasDiscountAccess

  const updatesLength = () =>
    (order?.details?.Deliveries?.length ?? 0) +
    (order?.details?.Refunds?.length ?? 0) +
    (order?.details?.Cancellations?.length ?? 0) +
    (order?.details?.Compensations?.length ?? 0) +
    (order?.details?.Releases?.length ?? 0) +
    (order?.details?.Modifications?.length ?? 0)

  const showGiftCard = () => {
    return (
      order?.details?.GiftCard?.providerId !== "" ||
      order?.details?.GiftCard?.providerName !== "" ||
      order?.details?.GiftCard?.status !== ""
    )
  }

  const AddressUpdatePending = (type: AddressType) => {
    if (modificationsAddresses.pending()) {
      return type === modificationsAddresses.getPendingModificationType()
    }
  }

  const getDiscountTabIndex = () => {
    let index = 3
    return index
  }

  const getUpdatesTabIndex = () => {
    let index = 3
    if (showDiscounts) index++
    if (showGiftCard()) index++
    if (showPromotions()) index++
    if (showVouchers()) index++
    if (order?.details?.Bonus) index++
    return index
  }

  const switchToDiscountTab = () => {
    if (showDiscounts) {
      setSelectedTabIndex(getDiscountTabIndex())
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  const switchToUpdatesTab = () => {
    if (updatesLength() > 0) {
      setSelectedTabIndex(getUpdatesTabIndex())
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  return (
    <Box padding="0" margin="0">
      <TopContainer>
        <GoBackLink to={"/orders"}>
          <ArrowLeft /> Back to orders
        </GoBackLink>
        <AcademyButton
          href={"https://brinkcommerce.featurebase.app/help/collections/6272544-orders"}
          target={"_blank"}
        >
          <AcademyIcon />
        </AcademyButton>
      </TopContainer>
      <>
        {showPopup && (
          <Popup
            title={"Are you sure?"}
            subtitle={"Do you want to resend order event for the following order:"}
            item={{ itemId: "", itemName: `#${order?.details?.reference}` }}
            type="default"
            buttonText="Yes"
            handleOkClick={() => {
              setResendOrderEventError("")
              resendOrderEvent({ variables: { orderId: id } })
            }}
            handleCloseClick={() => {
              setResendOrderEventError("")
              setShowPopup(false)
            }}
            loading={resendLoading}
            errorMessage={resendOrderEventError}
          />
        )}
      </>
      <Container className={loading ? "blur" : ""}>
        <Header>
          <div>
            <h1>Order #{order?.details?.reference ?? ""}</h1>
            <div>
              <Id>
                <span>{id}</span>
                <CopyButton string={id} />
              </Id>
              {order && (
                <Id>
                  <span>#{order?.details?.reference}</span>
                  <CopyButton string={order?.details?.reference} />
                </Id>
              )}
              {order?.details?.attributes?.merchantReference1 && (
                <MerchantRef>
                  <span>Merchant ref 1:</span>
                  <span>{order.details.attributes.merchantReference1}</span>
                  <CopyButton string={order.details.attributes.merchantReference1} />
                </MerchantRef>
              )}
              {order?.details?.attributes?.merchantReference2 && (
                <MerchantRef>
                  <span>Merchant ref 2:</span>
                  <span>{order.details.attributes.merchantReference2}</span>
                  <CopyButton string={order.details.attributes.merchantReference2} />
                </MerchantRef>
              )}
            </div>
          </div>
          <EditWrapper>
            <WithAuth requiredGroups={PermissionGroups.orders.write}>
              <ButtonWrapper>
                <SecondaryButton
                  handleClick={() => navigate(`/orders/create-order-from-existing-order/${id}`)}
                >
                  <CopyIcon />
                  Duplicate order
                </SecondaryButton>
                <SecondaryButton
                  disabled={cancellations.completed()}
                  handleClick={() => navigate(`/orders/manage-order/${id}`)}
                >
                  <SettingsIcon />
                  Update order
                </SecondaryButton>
              </ButtonWrapper>
            </WithAuth>
            <ActionButtonWrapper>
              <ActionDropdownButton iconButton={true}>
                <WithAuth requiredGroups={PermissionGroups.orders.execute}>
                  <li onClick={() => setShowPopup(true)}>
                    <ArrowIcon />
                    Resend order event
                  </li>
                </WithAuth>
                <li
                  onClick={() => {
                    getJson(order?.details)
                  }}
                >
                  <CodeIcon /> Get order JSON
                </li>
                <DeleteOrder orderDetails={order?.details} />
              </ActionDropdownButton>
            </ActionButtonWrapper>
          </EditWrapper>
        </Header>

        <OrderHeader
          order={order?.details}
          loading={loading}
          historyProps={{
            customStates,
            addCustomStateToHistory,
            setOrderHistoryList,
            orderHistoryList,
            setCustomStateList,
            customStateList,
            getCustomState
          }}
        />
        <Columns>
          <TabsContainer>
            <OrderTabProvider value={{ switchToDiscountTab, switchToUpdatesTab }}>
              <Tabs selectedIndex={selectedTabIndex} onSelect={setSelectedTabIndex}>
                <TabListWrapper>
                  <TabList>
                    <Tab>
                      Order details{" "}
                      {order?.details?.orderLines && order.details.orderLines.length > 0 && (
                        <b>({order.details.orderLines.length})</b>
                      )}
                    </Tab>
                    <Tab>
                      Shipping{" "}
                      {deliveries.completedDeliveries().length > 0 && (
                        <b>{`(${deliveries.completedDeliveries().length})`}</b>
                      )}{" "}
                      {AddressUpdatePending(AddressType.SHIPPING_ADDRESS) && <TabAlert />}
                    </Tab>
                    <Tab>
                      Payment {AddressUpdatePending(AddressType.BILLING_ADDRESS) && <TabAlert />}
                    </Tab>
                    {showDiscounts && <Tab>Discount</Tab>}
                    {showGiftCard() && <Tab>Gift card</Tab>}
                    {showPromotions() && <Tab>Promotion</Tab>}
                    {showVouchers() && <Tab>Vouchers</Tab>}
                    {order?.details?.Bonus && <Tab>Bonus</Tab>}
                    {updatesLength() > 0 && (
                      <Tab>
                        Updates <b>({updatesLength()})</b>
                      </Tab>
                    )}
                    <Tab>History log</Tab>
                  </TabList>
                </TabListWrapper>
                <TabPanel>
                  <OrderDetails
                    order={order?.details}
                    orderHistory={order?.history}
                    refetch={refetch}
                    refetchLoading={loading}
                    getCustomCode={getCustomCode}
                    storeGroups={storeGroupData?.getStoreGroups ?? []}
                    historyProps={{
                      customStates,
                      addCustomStateToHistory,
                      setOrderHistoryList,
                      orderHistoryList,
                      setCustomStateList,
                      customStateList,
                      getCustomState,
                      error: stateError
                    }}
                  />
                </TabPanel>
                <TabPanel>
                  <Shipping
                    order={order?.details}
                    refetch={refetch}
                    refetchLoading={loading}
                    getCustomCode={getCustomCode}
                  />
                </TabPanel>
                <TabPanel>
                  <OrderPayment
                    orderPayment={order?.details?.Payment}
                    billingAddress={order?.details?.billingAddress}
                    order={order?.details}
                    refetch={refetch}
                    refetchLoading={loading}
                  />
                </TabPanel>
                {showDiscounts && (
                  <TabPanel>
                    <OrderDiscount data={order?.details} />
                  </TabPanel>
                )}
                {showGiftCard() && (
                  <TabPanel>
                    <Giftcard orderGiftcard={order?.details?.GiftCard} />
                  </TabPanel>
                )}
                {showPromotions() && (
                  <TabPanel>
                    <Promotion promotionDetails={order?.details?.Promotion} />
                  </TabPanel>
                )}
                {showVouchers() && (
                  <TabPanel>
                    <Vouchers voucherDetails={order?.details?.Voucher} />
                  </TabPanel>
                )}
                {order?.details?.Bonus && (
                  <TabPanel>
                    <Bonus orderBonus={order?.details?.Bonus} />
                  </TabPanel>
                )}
                {updatesLength() > 0 && (
                  <TabPanel>
                    <Updates order={order?.details} />
                  </TabPanel>
                )}
                <TabPanel>
                  <OrderHistoryLog
                    orderHistory={orderHistoryList}
                    order={order?.details}
                    refetch={refetch}
                    customStates={customStates}
                    getCustomState={getCustomState}
                    addedCustomStates={customStateList}
                    addCustomStateToHistory={addCustomStateToHistory}
                    getCustomCode={getCustomCode}
                  />
                </TabPanel>
              </Tabs>
            </OrderTabProvider>
          </TabsContainer>
        </Columns>
      </Container>
    </Box>
  )
}
